import { useCallback, useState } from 'react'
import { flushSync } from 'react-dom'
import { useSearchParams } from 'react-router'
import { useApolloClient, useMutation } from '@apollo/client/react'

import { sanitizeAccessControl } from 'js/components/Form/AccessControlField'
import { PAGE_EDIT } from 'js/page/Edit.queries'
import { ADMIN_PAGES } from 'js/page/PageList.queries'

import { prependLatestPageEditorRevisionToCache } from './helpers/prependLatestPageEditorRevisionToCache'
import { sanitizeRows } from './helpers/sanitizeRows'
import { EDIT_PAGE_IN_EDITOR } from './PageEditor.queries'

const REFETCH_QUERIES = [ADMIN_PAGES, PAGE_EDIT]

type UsePageEditorSubmitParams = {
    guid: string
    rows: any[]
    entity: any
    accessControl: any[]
    isPublished: boolean
    setErrors: (errors?: any) => void
    setIsDirty: (isDirty: boolean) => void
    versionParam: string | null
}

const usePageEditorSubmit = ({
    guid,
    rows,
    entity,
    accessControl,
    isPublished,
    setErrors,
    setIsDirty,
    versionParam,
}: UsePageEditorSubmitParams) => {
    const [searchParams, setSearchParams] = useSearchParams()
    const [isSubmitting, setIsSubmitting] = useState(false)

    const [editPage] = useMutation<any>(EDIT_PAGE_IN_EDITOR)
    const client = useApolloClient()

    const submitForm = useCallback(
        async ({ isDraft }: { isDraft: boolean }) => {
            const timePublished = isDraft
                ? null
                : entity?.timePublished
                  ? entity.timePublished
                  : new Date().toISOString()

            setErrors()
            setIsSubmitting(true)

            try {
                await editPage({
                    variables: {
                        input: {
                            guid,
                            rows: sanitizeRows(rows),
                            timePublished,
                            accessControl: isDraft
                                ? null
                                : sanitizeAccessControl(accessControl),
                        },
                    },
                    refetchQueries: REFETCH_QUERIES,
                    awaitRefetchQueries: true,
                    update: async () => {
                        // Prepend the new revision instead of refetching the list,
                        // so fetch-more results stay in the version history popover.
                        await prependLatestPageEditorRevisionToCache(
                            client,
                            guid,
                        )
                    },
                })

                // Commit isDirty before removing ?v=. Otherwise the router blocker
                // still sees unsaved changes when the URL changes.
                flushSync(() => setIsDirty(false))

                if (versionParam) {
                    const nextSearchParams = new URLSearchParams(searchParams)
                    nextSearchParams.delete('v')
                    setSearchParams(nextSearchParams, { replace: true })
                }
            } catch (errors) {
                setErrors(errors)
            } finally {
                setIsSubmitting(false)
            }
        },
        [
            accessControl,
            client,
            editPage,
            entity?.timePublished,
            guid,
            rows,
            searchParams,
            setErrors,
            setIsDirty,
            setSearchParams,
            versionParam,
        ],
    )

    // Restore is the same as saving an active old revision as a new version.
    const restoreRevision = useCallback(() => {
        submitForm({ isDraft: !isPublished })
    }, [isPublished, submitForm])

    return {
        isSubmitting,
        submitForm,
        restoreRevision,
    }
}

export default usePageEditorSubmit
